import { Button, formatDate, formatPhone, ListView, PageHeader, useCursorList } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { SearchField } from "@majitap/ui/arc/search-field/search-field";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { customerActions } from "../../customers";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { useDebounced } from "../../search";
import { meQuery } from "../../session";
import { type Acquisition, AcquisitionFields } from "../shared/acquisition-fields";
import styles from "./customers.module.css";

export const customerFilters = filterSearch({ q: "text" });

export const CUSTOMER_STATUS = {
  active: ["Active", "success"],
  inactive: ["Inactive", "neutral"],
  suspended: ["Suspended", "warning"],
} as const;

/** Customers (P8-10, `CDm2p` in the design): find anyone by name or phone; add one. */
export function CustomersPage(): ReactNode {
  const search = useSearch({ from: "/console/customers" });
  const navigate = useNavigate({ from: "/customers" });
  const format = useCompanyFormat();
  const { data: me } = useSuspenseQuery(meQuery);
  const canManage = me?.permissions.includes("customers.manage") ?? false;
  const [typed, setTyped] = useState(search.q ?? "");
  const q = useDebounced(typed.trim());
  // the URL follows the search once typing settles
  useEffect(() => {
    if ((search.q ?? "") !== q) void navigate({ search: { q: q || undefined }, replace: true });
  }, [q, search.q, navigate]);
  const [adding, setAdding] = useState(false);
  const list = useCursorList({
    queryKey: ["customers", search.q ?? ""],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/customers", {
        params: { query: { q: search.q, cursor, limit: 25 } },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });

  return (
    <>
      <PageHeader
        title="Customers"
        description="People who order water, their places and tanks."
        actions={canManage ? <Button onClick={() => setAdding(true)}>Add customer</Button> : undefined}
      />
      <ListView
        caption="Customers"
        itemName={{ one: "customer", other: "customers" }}
        rows={list.rows}
        rowKey={(c) => c.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={Boolean(search.q)}
        onResetFilters={() => setTyped("")}
        empty={{ title: "No customers yet", description: "Customers sign up in the app, or staff add them here." }}
        toolbar={
          <div className={styles.searchBox}>
            <SearchField
              label="Search customers"
              value={typed}
              onValueChange={setTyped}
              placeholder="Name or phone number"
            />
          </div>
        }
        columns={[
          {
            key: "name",
            label: "Customer",
            render: (c) => (
              <Link to="/customers/$customerId" params={{ customerId: c.id }} className={styles.link}>
                {c.user.name}
              </Link>
            ),
          },
          { key: "phone", label: "Phone", render: (c) => formatPhone(c.user.phone_number) || "—" },
          {
            key: "businesses",
            label: "Businesses",
            render: (c) =>
              c.businesses
                .map((b) => (b.role === "user" ? b.business_name : `${b.business_name} (${b.role})`))
                .join(", ") || "—",
          },
          {
            key: "status",
            label: "Status",
            render: (c) => <Badge tone={CUSTOMER_STATUS[c.status][1]}>{CUSTOMER_STATUS[c.status][0]}</Badge>,
          },
          { key: "created_at", label: "Added", render: (c) => formatDate(c.created_at, format) },
        ]}
      />
      {adding ? <AddCustomerDialog onClose={() => setAdding(false)} /> : null}
    </>
  );
}

const E164 = /^\+[1-9]\d{7,14}$/;

/** Add a customer by phone. If the number already belongs to a customer here, open them instead. */
function AddCustomerDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("+254");
  const [email, setEmail] = useState("");
  const [won, setWon] = useState<Acquisition>({ source: null, staffId: null });
  const mutation = useMutation({
    mutationFn: () =>
      customerActions.create({
        name: name.trim(),
        phone_number: phone.replace(/\s/g, ""),
        email: email.trim() || undefined,
        acquisition_source: won.source ?? undefined,
        acquired_by_staff_id: won.staffId ?? undefined,
      }),
    onSuccess: (customer) => navigate({ to: "/customers/$customerId", params: { customerId: customer.id } }),
  });
  const error = mutation.error ? errorOf(mutation.error) : null;
  const existing = (error?.details as { customer_id?: string } | undefined)?.customer_id;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="Add customer" description="They can sign in to the customer app with this number.">
        <div className={styles.form}>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label="Phone number"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            description="International format, e.g. +254712345678."
            error={
              phone.length > 4 && !E164.test(phone.replace(/\s/g, ""))
                ? "Use the international format, e.g. +254712345678."
                : undefined
            }
          />
          <Input label="Email (optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <AcquisitionFields
            value={won}
            onChange={setWon}
            staffLabel="Won by"
            staffHelp="Their orders count as this staff member's sales, unless staff place an order themselves."
          />
          {error ? (
            <Alert tone={existing ? "info" : "danger"} title={existing ? "Already a customer" : "Not added"}>
              {existing ? (
                <Link to="/customers/$customerId" params={{ customerId: existing }} className={styles.link}>
                  Open their page
                </Link>
              ) : (
                messageFor(error)
              )}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!name.trim() || !E164.test(phone.replace(/\s/g, ""))}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Add customer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
